import { useState, useEffect } from "react";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { 
  Trophy, Target, TrendingUp, AlertTriangle, CheckCircle, 
  Settings, RefreshCw, User, Star, Clock
} from "lucide-react";
import { toast } from "sonner";

interface TopCandidatesViewProps {
  jobs: Array<{ id: string | number; title: string; status?: string }>;
  onCandidateSelect?: (candidateId: number) => void;
}

interface RankedCandidate {
  rank: number;
  candidateId: number;
  candidateName: string;
  candidateEmail: string;
  applicationId: number;
  applicationStatus: string;
  pipelineStage: string | null;
  interviewId: number;
  roleFitScore: number;
  competenceScore: number;
  communicationScore: number;
  overallScore: number;
  weightedScore: number;
  recommendation: string;
  aiRiskLevel: string;
  verificationStatus: string;
  appliedAt: string;
  roleTitle?: string;
}

interface RankingConfig {
  roleId: number;
  isEnabled: boolean;
  topCandidatesCount: number;
  minOverallScore: number;
  weightRoleFit: number;
  weightCompetence: number;
  weightCommunication: number;
}

export function TopCandidatesView({ jobs, onCandidateSelect }: TopCandidatesViewProps) {
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [showAllRoles, setShowAllRoles] = useState(true);
  const [topN, setTopN] = useState(10);
  const [minScore, setMinScore] = useState(60);
  const [candidates, setCandidates] = useState<RankedCandidate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  
  const [config, setConfig] = useState<RankingConfig>({
    roleId: 0,
    isEnabled: false,
    topCandidatesCount: 10,
    minOverallScore: 60,
    weightRoleFit: 40,
    weightCompetence: 30,
    weightCommunication: 30,
  });

  const fetchTopCandidates = async () => {
    setIsLoading(true);
    try {
      let url: string;
      if (showAllRoles || !selectedRoleId) {
        url = `/api/auto-ranking/top-candidates-all?limit=${topN}&minScore=${minScore}`;
      } else {
        url = `/api/auto-ranking/top-candidates/${selectedRoleId}?limit=${topN}&minScore=${minScore}`;
      }

      const res = await apiRequest("GET", url);
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to fetch candidates");
      }

      setCandidates(data.topCandidates || []);
      if (data.config) {
        setConfig(data.config);
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to load top candidates");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchConfig = async (roleId: string) => {
    try {
      const res = await apiRequest("GET", `/api/auto-ranking/config/${roleId}`);
      const data = await res.json();
      if (res.ok) {
        setConfig(data);
      }
    } catch (error) {
      console.error("Failed to load config:", error);
    }
  };

  const saveConfig = async () => {
    if (!selectedRoleId) {
      toast.error("Please select a role first");
      return;
    }

    try {
      const res = await apiRequest("POST", `/api/auto-ranking/config/${selectedRoleId}`, config);

      if (!res.ok) {
        throw new Error("Failed to save config");
      }

      toast.success("Ranking config saved");
      fetchTopCandidates();
    } catch (error) {
      toast.error("Failed to save configuration");
    }
  };

  useEffect(() => {
    fetchTopCandidates();
  }, [showAllRoles, selectedRoleId, topN, minScore]);

  useEffect(() => {
    if (selectedRoleId) {
      fetchConfig(selectedRoleId);
    }
  }, [selectedRoleId]);

  const activeJobs = jobs.filter(j => {
    const status = (j.status || "").toLowerCase();
    return !["closed", "expired", "filled", "cancelled"].includes(status);
  });

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-yellow-500";
    return "text-red-500";
  };

  const getRiskBadge = (risk: string) => {
    const level = risk?.toLowerCase() || "unknown";
    if (level === "low") return <Badge className="bg-green-500">Low Risk</Badge>;
    if (level === "medium") return <Badge className="bg-yellow-500">Medium Risk</Badge>;
    if (level === "high") return <Badge className="bg-red-500">High Risk</Badge>;
    return <Badge variant="outline">Unknown</Badge>;
  };

  const getRecommendationBadge = (rec: string) => {
    const r = rec?.toLowerCase() || "";
    if (r.includes("proceed")) return <Badge className="bg-green-500">Proceed</Badge>;
    if (r.includes("hold")) return <Badge className="bg-yellow-500">Hold</Badge>;
    if (r.includes("decline")) return <Badge className="bg-red-500">Decline</Badge>;
    return <Badge variant="outline">{rec || "Pending"}</Badge>;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              checked={showAllRoles}
              onCheckedChange={setShowAllRoles}
              data-testid="toggle-all-roles"
            />
            <Label className="text-sm">All Roles</Label>
          </div>

          {!showAllRoles && (
            <Select value={selectedRoleId} onValueChange={setSelectedRoleId}>
              <SelectTrigger className="w-[200px]" data-testid="select-role-ranking">
                <SelectValue placeholder="Select role..." />
              </SelectTrigger>
              <SelectContent>
                {activeJobs.map((job) => (
                  <SelectItem key={job.id} value={job.id.toString()}>
                    {job.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label className="text-sm whitespace-nowrap">Top</Label>
            <Select value={topN.toString()} onValueChange={(v) => setTopN(parseInt(v))}>
              <SelectTrigger className="w-20" data-testid="select-top-n">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[5, 10, 20, 50, 100].map(n => (
                  <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Label className="text-sm whitespace-nowrap">Min Score</Label>
            <Input
              type="number"
              value={minScore}
              onChange={(e) => setMinScore(parseInt(e.target.value) || 0)}
              className="w-16"
              min={0}
              max={100}
              data-testid="input-min-score"
            />
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowConfig(!showConfig)}
            data-testid="toggle-config"
          >
            <Settings className="w-4 h-4" />
          </Button>

          <Button
            size="sm"
            onClick={fetchTopCandidates}
            disabled={isLoading}
            data-testid="refresh-candidates"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {showConfig && !showAllRoles && selectedRoleId && (
        <Card className="p-4 bg-muted/30">
          <h4 className="font-medium mb-3 flex items-center gap-2">
            <Target className="w-4 h-4" />
            Auto-Ranking Configuration for Role
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <Label className="text-xs text-muted-foreground">Role Fit Weight</Label>
              <div className="flex items-center gap-2">
                <Slider
                  value={[config.weightRoleFit]}
                  onValueChange={([v]) => setConfig({ ...config, weightRoleFit: v })}
                  max={100}
                  step={5}
                  className="flex-1"
                />
                <span className="text-sm font-medium w-10">{config.weightRoleFit}%</span>
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Competence Weight</Label>
              <div className="flex items-center gap-2">
                <Slider
                  value={[config.weightCompetence]}
                  onValueChange={([v]) => setConfig({ ...config, weightCompetence: v })}
                  max={100}
                  step={5}
                  className="flex-1"
                />
                <span className="text-sm font-medium w-10">{config.weightCompetence}%</span>
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Communication Weight</Label>
              <div className="flex items-center gap-2">
                <Slider
                  value={[config.weightCommunication]}
                  onValueChange={([v]) => setConfig({ ...config, weightCommunication: v })}
                  max={100}
                  step={5}
                  className="flex-1"
                />
                <span className="text-sm font-medium w-10">{config.weightCommunication}%</span>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Switch
                checked={config.isEnabled}
                onCheckedChange={(v) => setConfig({ ...config, isEnabled: v })}
              />
              <Label className="text-sm">Enable auto-ranking for this role</Label>
            </div>
            <Button size="sm" onClick={saveConfig} data-testid="save-config">
              Save Configuration
            </Button>
          </div>
        </Card>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : candidates.length === 0 ? (
        <Card className="p-8 text-center">
          <Trophy className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-30" />
          <p className="text-muted-foreground">No candidates match the criteria</p>
          <p className="text-sm text-muted-foreground mt-1">
            Adjust the minimum score or wait for more interviews to complete
          </p>
        </Card>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead>Candidate</TableHead>
                {showAllRoles && <TableHead>Role</TableHead>}
                <TableHead className="text-center">Overall</TableHead>
                <TableHead className="text-center">Role Fit</TableHead>
                <TableHead className="text-center">Competence</TableHead>
                <TableHead className="text-center">Communication</TableHead>
                <TableHead>AI Risk</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {candidates.map((c) => (
                <TableRow 
                  key={`${c.candidateId}-${c.applicationId}`} 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => onCandidateSelect?.(c.candidateId)}
                  data-testid={`row-candidate-${c.candidateId}`}
                >
                  <TableCell>
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10">
                      {c.rank <= 3 ? (
                        <Trophy className={`w-4 h-4 ${c.rank === 1 ? "text-yellow-500" : c.rank === 2 ? "text-gray-400" : "text-amber-600"}`} />
                      ) : (
                        <span className="text-sm font-medium">{c.rank}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{c.candidateName}</p>
                        <p className="text-xs text-muted-foreground">{c.candidateEmail}</p>
                      </div>
                    </div>
                  </TableCell>
                  {showAllRoles && (
                    <TableCell>
                      <span className="text-sm">{c.roleTitle}</span>
                    </TableCell>
                  )}
                  <TableCell className="text-center">
                    <span className={`text-lg font-bold ${getScoreColor(c.overallScore)}`}>
                      {c.overallScore}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className={getScoreColor(c.roleFitScore)}>{c.roleFitScore}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className={getScoreColor(c.competenceScore)}>{c.competenceScore}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className={getScoreColor(c.communicationScore)}>{c.communicationScore}</span>
                  </TableCell>
                  <TableCell>
                    {getRiskBadge(c.aiRiskLevel)}
                  </TableCell>
                  <TableCell>
                    {getRecommendationBadge(c.recommendation)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          Showing {candidates.length} candidate{candidates.length !== 1 ? "s" : ""} 
          {minScore > 0 && ` with score ≥${minScore}`}
        </span>
        <span className="flex items-center gap-1">
          <TrendingUp className="w-4 h-4" />
          Sorted by overall score (highest first)
        </span>
      </div>
    </div>
  );
}
